/**
 * /dashboard/settings/billing — Settings → Organization → Billing
 * (Phase 8.4).
 *
 *  - Shows the caller's own tenant subscription
 *    (GET /api/v1/billing/subscription): plan, status badge, started /
 *    renews-or-expires dates. A trial tenant resolves to
 *    { status: "trial", plan: null }.
 *  - For a company_admin: an upgrade / change-plan section that reuses
 *    the marketing pricing cards (<PlanPicker> → <PricingCards
 *    mode="billing">) and the SAME checkout flow (useCheckout). The
 *    current plan is shown as "Current plan" (disabled).
 *  - For a non-admin HR (hr_manager — Settings/Billing is HR-admin
 *    gated in nav): READ-ONLY. The picker renders but every CTA is
 *    "Ask your admin" (disabled). The backend re-checks company_admin +
 *    CSRF on every billing write — this is presentation only.
 *  - 503 billing_unavailable on checkout is the EXPECTED state in this
 *    build (Razorpay creds intentionally unset); the picker swaps the
 *    CTA to a graceful "Contact sales" link, NOT an error toast.
 *
 * Auth is cookie-only via the shared client; nothing here touches
 * localStorage / Authorization.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port
 * of frontend/src/app/(dashboard)/dashboard/settings/billing/page.tsx.
 */

import { createFileRoute } from "@tanstack/react-router";
import { CreditCard } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/hooks/use-billing";
import { toApiError } from "@/lib/api";
import { formatPaise } from "@/lib/expense-format";
import { fmtDate } from "@/lib/leave-format";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { StatusBadge } from "@/components/shared/status-badge";
import { PlanPicker } from "@/components/marketing/plan-picker";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/billing",
)({
  component: SettingsBillingRoute,
});

function SettingsBillingRoute() {
  const { user } = useAuth();
  const sub = useSubscription();

  const isCompanyAdmin = user?.role === "company_admin";
  const prefill = user
    ? {
        name: `${user.first_name} ${user.last_name}`.trim(),
        email: user.email,
      }
    : undefined;

  const subscription = sub.data?.subscription;
  const plan = subscription?.plan ?? null;
  // A trial / no-plan tenant: status is the source of truth, plan null.
  const isTrial = subscription?.status === "trial" || plan == null;

  // The expires_at date is a RENEWAL date while active, and an EXPIRY
  // when the subscription has lapsed/cancelled — label accordingly.
  const dateLabel =
    subscription?.status === "active" ? "Renews on" : "Access until";

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Billing</h2>
            <p className="text-sm text-muted-foreground">
              Your subscription and plan.
            </p>
          </div>

          {/* Current subscription */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4 text-primary" />
                Current subscription
              </CardTitle>
              <CardDescription>
                The plan and status for your organisation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sub.isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ) : sub.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>
                    Couldn&apos;t load your subscription
                  </AlertTitle>
                  <AlertDescription>
                    {toApiError(sub.error).message ||
                      "Please refresh and try again."}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="text-lg font-semibold text-gray-900">
                        {plan ? plan.name : "Trial"}
                      </p>
                      {subscription && (
                        <StatusBadge status={subscription.status} />
                      )}
                    </div>

                    {plan ? (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {formatPaise(plan.price_paise)}
                        {plan.billing_period === "monthly"
                          ? " / month"
                          : plan.billing_period === "yearly"
                            ? " / year"
                            : ` / ${plan.billing_period}`}
                        {" · "}
                        {plan.max_employees === 0
                          ? "Unlimited employees"
                          : `Up to ${plan.max_employees} employees`}
                      </p>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        You&apos;re on a trial.{" "}
                        {isCompanyAdmin
                          ? "Choose a plan below to activate your subscription."
                          : "Ask your company admin to choose a plan."}
                      </p>
                    )}

                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      {subscription?.started_at && (
                        <p>
                          Started:{" "}
                          <span className="font-medium text-gray-700">
                            {fmtDate(subscription.started_at)}
                          </span>
                        </p>
                      )}
                      {subscription?.expires_at && (
                        <p>
                          {dateLabel}:{" "}
                          <span className="font-medium text-gray-700">
                            {fmtDate(subscription.expires_at)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change / choose a plan */}
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-gray-900">
              {isTrial ? "Choose a plan" : "Change plan"}
            </h2>
            <p className="mb-6 mt-1 text-sm text-muted-foreground">
              {isCompanyAdmin
                ? "Pick the plan that fits your team. You'll be taken to secure checkout."
                : "Only your company admin can change the subscription. You can review the available plans below."}
            </p>

            {!sub.isLoading && (
              <PlanPicker
                isCompanyAdmin={isCompanyAdmin}
                currentPlanCode={plan?.code ?? null}
                prefill={prefill}
                onSubscribed={() => sub.refetch()}
              />
            )}

            <p className="mt-8 text-xs text-muted-foreground">
              All prices are exclusive of applicable GST (currently 18%).
              Payments are processed securely by Razorpay. See our{" "}
              <a
                href="/refund"
                className="font-medium text-primary hover:text-primary/80"
              >
                refund &amp; cancellation policy
              </a>
              .
            </p>
          </div>
        </div>
      </SettingsShell>
    </>
  );
}
